import type { BenefitsTaskAnswers } from "@/shared/hooks/useBenefitsTaskAnswers";
import type { DeceasedUserData } from "@/shared/hooks/useDeceasedUserData";
import type { IdExtractionData } from "@/shared/hooks/useUserIdExtraction";
import type { UserSettingsData } from "@/shared/hooks/useUserSettingsData";
import { toStateAbbreviation } from "@/shared/utils/addressUtils";
import { formatPhoneInput } from "@/shared/utils/formatters";

export interface ExtraFieldSources {
  benefits?: BenefitsTaskAnswers;
  relationship?: string;
  deceasedSSN?: string | null;
  deceasedPassportNumber?: string | null;
  userSSN?: string | null;
  userDOB?: string | null;
}

type FieldAccessor = (
  deceased: DeceasedUserData,
  user: UserSettingsData,
  idData: IdExtractionData | null,
  extra: ExtraFieldSources,
) => string;

const FIELD_SOURCES: Record<string, FieldAccessor> = {
  // Deceased person data
  deceased_first_name: (d) => d.firstName ?? "",
  deceased_last_name: (d) => d.lastName ?? "",
  deceased_full_name: (d) => d.fullName ?? "",
  date_of_death: (d) => d.dateOfDeath ?? "",
  date_of_birth: (d) => d.dateOfBirth ?? "",
  deceased_address: (d) => d.address ?? "",
  deceased_apartment: (d) => d.apartment ?? "",
  deceased_city: (d) => d.city ?? "",
  deceased_state: (d) => toStateAbbreviation(d.state ?? ""),
  deceased_zip: (d) => d.zipCode ?? "",
  deceased_county: (d) => d.county ?? "",
  place_of_death: (d) => d.placeOfDeath ?? "",
  funeral_home_reported: (d) => (d.funeralHomeReported ? "yes" : "no"),
  marriage_date: (d) => d.marriageDate ?? "",
  marriage_place: (d) => d.marriagePlace ?? "",

  // Deceased SSN (decrypted)
  deceased_ss_number: (_d, _u, _id, extra) => extra.deceasedSSN ?? "",

  // Deceased passport number (decrypted)
  deceased_passport_number: (_d, _u, _id, extra) => extra.deceasedPassportNumber ?? "",

  // Logged-in user data (ID extraction takes priority over stored address)
  user_full_name: (_d, u, id) => {
    const first = id?.firstName || u.firstName;
    const last = id?.lastName || u.lastName;
    return [first, last].filter(Boolean).join(" ");
  },
  user_first_name: (_d, u, id) => id?.firstName || u.firstName,
  user_last_name: (_d, u, id) => id?.lastName || u.lastName,
  user_email: (_d, u) => u.email,
  user_phone: (_d, u) => formatPhoneInput(u.phoneNumber),
  user_address: (_d, u, id) => u.addressParts.streetAddress || id?.address || "",
  user_apartment: (_d, u) => u.addressParts.apartment,
  user_city: (_d, u, id) =>
    u.addressIsCanonical ? u.addressParts.city : id?.city || u.addressParts.city,
  user_state: (_d, u, id) =>
    toStateAbbreviation(
      u.addressIsCanonical ? u.addressParts.state : id?.state || u.addressParts.state,
    ),
  user_zip: (_d, u, id) =>
    u.addressIsCanonical ? u.addressParts.zipCode : id?.zip || u.addressParts.zipCode,
  user_ss_number: (_d, _u, _id, extra) => extra.userSSN ?? "",
  user_dob: (_d, _u, _id, extra) => extra.userDOB ?? "",
  user_relationship: (_d, _u, _id, extra) => extra.relationship ?? "",

  // Benefits task answers (military + employment)
  branch_of_service: (_d, _u, _id, extra) => extra.benefits?.military?.branch ?? "",
  service_start_date: (_d, _u, _id, extra) => extra.benefits?.military?.startYear ?? "",
  service_end_date: (_d, _u, _id, extra) => extra.benefits?.military?.endYear ?? "",
  discharge_status: (_d, _u, _id, extra) => extra.benefits?.military?.dischargeStatus ?? "",
  employer: (_d, _u, _id, extra) => {
    const employers = extra.benefits?.employers ?? [];
    return employers.length > 0 ? employers[0].name : "";
  },
};

export function resolveFieldValue(
  autoPopulateKey: string | undefined,
  deceased: DeceasedUserData,
  user: UserSettingsData,
  idData?: IdExtractionData | null,
  extra?: ExtraFieldSources,
): string {
  if (!autoPopulateKey) return "";
  const accessor = FIELD_SOURCES[autoPopulateKey];
  if (!accessor) return "";
  return accessor(deceased, user, idData ?? null, extra ?? {}) ?? "";
}
